'use client';

import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { cn } from '@module/lib/utils';
import useModalStore from '@module/stores/useModalStore';
import { usePathname, useRouter } from 'next/navigation';
import { type ReactNode, useCallback } from 'react';

interface RouteTabsItemProps {
  children: ReactNode;
  path: string;
  className?: string;
  subPath?: string;
  isAuthCheck?: boolean;
  onClick?: () => void;
  disableActiveStyle?: boolean;
  matchingPaths?: string[];
}

function RouteTabsItem({
  children,
  path,
  className,
  subPath = '',
  isAuthCheck = false,
  onClick,
  disableActiveStyle = false,
  matchingPaths,
}: RouteTabsItemProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { isLogin } = useGetLoginInfoQuery();
  const pushModal = useModalStore((state) => state.pushModal);
  const requestLogin = () => {
    if (aspCond.if('new-asp')) {
      SettingConfig.aspUtils?.goLogin();
      return;
    }

    pushModal({
      type: 'AUTH_MODAL',
      id: 'RouteTabsItem',
    });
  };

  const isCurrentTab = pathname.includes(path);

  const isMatchingPath = matchingPaths?.some((matchPath) => pathname.includes(matchPath)) ?? false;

  const isActiveCurrentTabStyle = disableActiveStyle ? false : isCurrentTab || isMatchingPath;
  const buildPathWithParams = useCallback(() => {
    return `${path}${subPath}`;
  }, [path, subPath]);

  const handleClick = () => {
    if (isAuthCheck && !isLogin) {
      requestLogin();
      return;
    }

    onClick?.();
    router.push(buildPathWithParams());
  };

  return (
    <button
      type='button'
      className={cn(
        'mr-10 border-b-0 pb-4 text-colorFg01',
        {
          'border-t-4 border-t-transparent border-b-4 border-b-colorFg01': isActiveCurrentTabStyle,
        },
        className,
      )}
      onClick={handleClick}
    >
      <p
        className={cn('mx-1 whitespace-nowrap', {
          'font-[500]': isActiveCurrentTabStyle,
        })}
      >
        {children}
      </p>
    </button>
  );
}

export default RouteTabsItem;
